import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { lessonLogAPI, LessonLog as LessonLogType, getYoutubeThumbnail } from '../../services/lessonLogAPI';
import { userAPI } from '../../services';
import TeacherPageHeader from '../../components/TeacherPageHeader';
import toast from 'react-hot-toast';
import '../../styles/pages/teacher/_lesson-log-list.scss';

// URL을 링크로 변환하는 함수
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="comment-link"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

interface Student {
  id: number;
  name: string;
  lessonType?: string;
  lessonDayOfWeek?: string;
  lessonStartTime?: string;
}

// 이번주 특정 요일의 날짜 계산
const getThisWeekDate = (dayOfWeek: string): string => {
  const dayMap: Record<string, number> = {
    'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
    'thursday': 4, 'friday': 5, 'saturday': 6
  };

  const today = new Date();
  const currentDay = today.getDay();
  const targetDay = dayMap[dayOfWeek.toLowerCase()] ?? 0;

  // 이번주 해당 요일 날짜 계산
  const diff = targetDay - currentDay;
  const targetDate = new Date(today);
  targetDate.setDate(today.getDate() + diff);

  return targetDate.toISOString().split('T')[0];
};

const LessonLog: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [logs, setLogs] = useState<LessonLogType[]>([]);
  const [selectedLog, setSelectedLog] = useState<LessonLogType | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadCounts, setUnreadCounts] = useState<Record<number, number>>({});
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [thisWeekDate, setThisWeekDate] = useState<string | null>(null);

  // 모바일 화면 단계: 'students' | 'logs' | 'detail'
  const [mobileView, setMobileView] = useState<'students' | 'logs' | 'detail'>('students');

  // 브라우저 뒤로가기(Android 시스템 백 버튼) 처리
  const handlePopState = useCallback((e: PopStateEvent) => {
    const view = e.state?.view;
    if (view === 'logs') {
      setMobileView('logs');
    } else {
      setMobileView('students');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  // 폼 데이터
  const [formData, setFormData] = useState({
    lessonDate: format(new Date(), 'yyyy-MM-dd'),
    lessonTime: '',
    song: '',
    songYoutubeUrl: '',
    content: '',
    homework: '',
    teacherNote: ''
  });

  useEffect(() => {
    loadStudents();
    loadUnreadCounts();
  }, []);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getUsers({ status: 'active', limit: 200 });
      if (response.success && response.data?.users) {
        setStudents(response.data.users);
      }
    } catch {
      toast.error('학생 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await lessonLogAPI.getUnreadCounts();
      if (response.success) {
        setUnreadCounts(response.data || {});
      }
    } catch {
      console.error('읽지 않은 댓글 수 조회 실패');
    }
  };

  const loadStudentLogs = async (studentId: number, lessonDayOfWeek?: string) => {
    setLogsLoading(true);
    try {
      const response = await lessonLogAPI.getByStudent(studentId, { limit: 50 });
      if (response.success) {
        const loadedLogs = response.data || [];
        setLogs(loadedLogs);

        // 이번주 수업 날짜 계산 (lessonDayOfWeek가 있는 경우)
        if (lessonDayOfWeek) {
          const weekDate = getThisWeekDate(lessonDayOfWeek);
          // 이미 해당 날짜에 수업 기록이 있는지 확인
          const exists = loadedLogs.some((log: LessonLogType) => log.lessonDate === weekDate);
          setThisWeekDate(exists ? null : weekDate);
        } else {
          setThisWeekDate(null);
        }
      }
    } catch {
      toast.error('수업 기록을 불러오는데 실패했습니다.');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleStudentClick = (student: Student) => {
    setSelectedStudent(student);
    setSelectedLog(null);
    setIsCreatingNew(false);
    setThisWeekDate(null);
    window.history.pushState({ view: 'logs' }, '');
    setMobileView('logs');
    loadStudentLogs(student.id, student.lessonDayOfWeek);
  };

  const handleLogClick = async (log: LessonLogType) => {
    setSelectedLog(log);
    setIsCreatingNew(false);
    window.history.pushState({ view: 'detail' }, '');
    setMobileView('detail');
    setReplyText('');
    setFormData({
      lessonDate: log.lessonDate,
      lessonTime: log.lessonTime?.slice(0, 5) || '',
      song: log.song || '',
      songYoutubeUrl: log.songYoutubeUrl || '',
      content: log.content || '',
      homework: log.homework || '',
      teacherNote: log.teacherNote || ''
    });

    // 읽지 않은 학생 댓글이 있으면 읽음 처리
    const hasUnreadStudentComments = log.comments?.some(
      c => c.authorType === 'student' && !c.isReadByTeacher
    );
    if (hasUnreadStudentComments) {
      try {
        await lessonLogAPI.markLogAsRead(log.id);
        // 로컬 상태 업데이트
        setLogs(prevLogs =>
          prevLogs.map(l => {
            if (l.id === log.id && l.comments) {
              return {
                ...l,
                comments: l.comments.map(c =>
                  c.authorType === 'student' ? { ...c, isReadByTeacher: true } : c
                )
              };
            }
            return l;
          })
        );
        loadUnreadCounts(); // 배지 업데이트
        // 사이드바 카운트 갱신 이벤트
        window.dispatchEvent(new CustomEvent('lessonLogRead'));
      } catch {
        console.error('읽음 처리 실패');
      }
    }
  };

  const handleNewLog = () => {
    setSelectedLog(null);
    setIsCreatingNew(true);
    window.history.pushState({ view: 'detail' }, '');
    setMobileView('detail');
    setReplyText('');
    setFormData({
      lessonDate: format(new Date(), 'yyyy-MM-dd'),
      lessonTime: selectedStudent?.lessonStartTime?.slice(0, 5) || '',
      song: '',
      songYoutubeUrl: '',
      content: '',
      homework: '',
      teacherNote: ''
    });
  };

  // 지금 기준으로 가장 가까운 00분/30분 시각 반환
  const getNearestHalfHour = () => {
    const now = new Date();
    const rounded = new Date(now);
    rounded.setSeconds(0, 0);
    const minutes = now.getMinutes();
    if (minutes < 15) {
      rounded.setMinutes(0);
    } else if (minutes < 45) {
      rounded.setMinutes(30);
    } else {
      rounded.setMinutes(0);
      rounded.setHours(rounded.getHours() + 1);
    }
    return rounded;
  };

  // 이번주 수업 껍데기 클릭 시 생성 — 지금 기준 날짜 + 가장 가까운 00/30분으로 세팅
  const handleThisWeekClick = async () => {
    if (!selectedStudent || !thisWeekDate) return;

    setLogsLoading(true);
    try {
      const nearest = getNearestHalfHour();
      const response = await lessonLogAPI.save({
        studentId: selectedStudent.id,
        lessonDate: format(nearest, 'yyyy-MM-dd'),
        lessonTime: format(nearest, 'HH:mm'),
        status: 'draft'
      });

      if (response.success) {
        setThisWeekDate(null);
        // 새로 생성된 기록을 선택 상태로
        setLogs(prev => [response.data, ...prev].sort((a, b) =>
          b.lessonDate.localeCompare(a.lessonDate)
        ));
        // 폼 데이터 설정
        setSelectedLog(response.data);
        setIsCreatingNew(false);
        setFormData({
          lessonDate: response.data.lessonDate,
          lessonTime: response.data.lessonTime?.slice(0, 5) || '',
          song: '',
          songYoutubeUrl: '',
          content: '',
          homework: '',
          teacherNote: ''
        });
        toast.success('수업 기록이 생성되었습니다.');
      }
    } catch {
      toast.error('수업 기록 생성에 실패했습니다.');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleAddReply = async () => {
    if (!selectedLog || !replyText.trim()) return;

    setSubmittingReply(true);
    try {
      const response = await lessonLogAPI.addComment(selectedLog.id, replyText.trim());
      if (response.success) {
        toast.success('답글이 등록되었습니다.');
        setReplyText('');
        // 로컬 상태 업데이트
        const updatedLog = {
          ...selectedLog,
          comments: [...(selectedLog.comments || []), response.data]
        };
        setSelectedLog(updatedLog);
        setLogs(prevLogs =>
          prevLogs.map(l => l.id === selectedLog.id ? updatedLog : l)
        );
      }
    } catch {
      toast.error('답글 등록에 실패했습니다.');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 댓글 수정 시작
  const handleStartEdit = (comment: { id: number; message: string }) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.message);
  };

  // 댓글 수정 취소
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  // 댓글 수정 저장
  const handleSaveEdit = async () => {
    if (!editingCommentId || !editCommentText.trim()) return;

    try {
      const response = await lessonLogAPI.updateComment(editingCommentId, editCommentText.trim());
      if (response.success) {
        toast.success('댓글이 수정되었습니다.');
        // 로컬 상태 업데이트
        if (selectedLog) {
          const updatedLog = {
            ...selectedLog,
            comments: selectedLog.comments?.map(c =>
              c.id === editingCommentId ? { ...c, message: editCommentText.trim() } : c
            )
          };
          setSelectedLog(updatedLog);
          setLogs(prevLogs =>
            prevLogs.map(l => l.id === selectedLog.id ? updatedLog : l)
          );
        }
        handleCancelEdit();
      }
    } catch {
      toast.error('댓글 수정에 실패했습니다.');
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;

    try {
      const response = await lessonLogAPI.deleteComment(commentId);
      if (response.success) {
        toast.success('댓글이 삭제되었습니다.');
        // 로컬 상태 업데이트
        if (selectedLog) {
          const updatedLog = {
            ...selectedLog,
            comments: selectedLog.comments?.filter(c => c.id !== commentId)
          };
          setSelectedLog(updatedLog);
          setLogs(prevLogs =>
            prevLogs.map(l => l.id === selectedLog.id ? updatedLog : l)
          );
        }
      }
    } catch {
      toast.error('댓글 삭제에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!selectedLog || !selectedStudent) return;
    if (!window.confirm('이 수업 기록을 삭제하시겠습니까?')) return;

    try {
      const response = await lessonLogAPI.delete(selectedLog.id);
      if (response.success) {
        toast.success('수업 기록이 삭제되었습니다.');
        setSelectedLog(null);
        setIsCreatingNew(false);
        window.history.back();
        loadStudentLogs(selectedStudent.id, selectedStudent.lessonDayOfWeek);
      }
    } catch {
      toast.error('수업 기록 삭제에 실패했습니다.');
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (publish: boolean = false) => {
    if (!selectedStudent) return;

    setSaving(true);
    try {
      const response = await lessonLogAPI.save({
        studentId: selectedStudent.id,
        lessonDate: formData.lessonDate,
        lessonTime: formData.lessonTime || undefined,
        song: formData.song,
        songYoutubeUrl: formData.songYoutubeUrl,
        content: formData.content,
        homework: formData.homework,
        teacherNote: formData.teacherNote,
        status: publish ? 'published' : 'draft',
        logId: selectedLog?.id  // 기존 기록 수정 시 ID 전달
      });

      if (response.success) {
        toast.success(publish ? '수업 기록이 발행되었습니다.' : '임시저장 되었습니다.');
        loadStudentLogs(selectedStudent.id);
        if (response.data) {
          setSelectedLog(response.data);
          setIsCreatingNew(false);
        }
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err.response?.data?.message || '저장에 실패했습니다.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // 요일 순서 정의 (월→일) - 영어/한글 모두 지원
  const dayOrder: Record<string, number> = {
    'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3, 'friday': 4, 'saturday': 5, 'sunday': 6,
    '월': 0, '화': 1, '수': 2, '목': 3, '금': 4, '토': 5, '일': 6
  };

  const filteredStudents = students
    .filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // 요일 비교
      const dayA = a.lessonDayOfWeek ? dayOrder[a.lessonDayOfWeek.toLowerCase()] ?? 99 : 99;
      const dayB = b.lessonDayOfWeek ? dayOrder[b.lessonDayOfWeek.toLowerCase()] ?? 99 : 99;
      if (dayA !== dayB) return dayA - dayB;

      // 같은 요일이면 시간순
      const timeA = a.lessonStartTime || '99:99';
      const timeB = b.lessonStartTime || '99:99';
      return timeA.localeCompare(timeB);
    });

  const currentThumbnail = formData.songYoutubeUrl ? getYoutubeThumbnail(formData.songYoutubeUrl) : null;

  if (loading) {
    return (
      <div className="lesson-log-list-page">
        <div className="page-loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className={`lesson-log-list-page mobile-${mobileView}`}>
      {/* 상단 헤더 바 (데스크탑) */}
      <TeacherPageHeader
        title="수업기록"
        subtitle={selectedStudent ? `학생 목록 › ${selectedStudent.name} › 수업 기록` : '학생 목록'}
      />

      <div className="log-panels">
      {/* 학생 목록 */}
      <div className="student-list-panel">
        <div className="panel-header">
          <h2>학생 목록</h2>
          <span className="count">{students.length}명</span>
        </div>
        <div className="search-box">
          <input
            type="text"
            placeholder="학생 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="student-list">
          {filteredStudents.map(student => (
            <div
              key={student.id}
              className={`student-item ${selectedStudent?.id === student.id ? 'active' : ''}`}
              onClick={() => handleStudentClick(student)}
            >
              <div className="student-info">
                <span className="student-name">{student.name}</span>
                {student.lessonDayOfWeek && (
                  <span className="student-schedule">
                    {student.lessonDayOfWeek} {student.lessonStartTime?.slice(0, 5)}
                  </span>
                )}
              </div>
              {unreadCounts[student.id] > 0 && (
                <span className="unread-badge">{unreadCounts[student.id]}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 수업 기록 목록 */}
      <div className="log-list-panel">
        <div className="panel-header">
          <button className="btn-back" onClick={() => window.history.back()}>←</button>
          <h2>{selectedStudent ? `${selectedStudent.name}의 수업 기록` : '학생을 선택하세요'}</h2>
          {selectedStudent && (
            <button className="btn-new" onClick={handleNewLog}>
              + 새 기록
            </button>
          )}
        </div>
        <div className="log-list">
          {!selectedStudent ? (
            <div className="empty-msg">왼쪽에서 학생을 선택하세요</div>
          ) : logsLoading ? (
            <div className="loading-msg">로딩 중...</div>
          ) : logs.length === 0 && !thisWeekDate ? (
            <div className="empty-msg">수업 기록이 없습니다</div>
          ) : (
            <>
              {/* 이번주 수업 껍데기 */}
              {thisWeekDate && (
                <div
                  className="log-item placeholder"
                  onClick={handleThisWeekClick}
                >
                  <div className="log-date">
                    {format(new Date(thisWeekDate), 'yy.MM.dd (E)', { locale: ko })}
                  </div>
                  <div className="log-song">(이번주 수업)</div>
                  <span className="log-status draft">클릭하여 생성</span>
                </div>
              )}
              {logs.map(log => {
                const unreadCount = log.comments?.filter(
                  c => c.authorType === 'student' && !c.isReadByTeacher
                ).length || 0;
                return (
                  <div
                    key={log.id}
                    className={`log-item ${selectedLog?.id === log.id ? 'active' : ''}`}
                    onClick={() => handleLogClick(log)}
                  >
                    <div className="log-date">
                      {format(new Date(log.lessonDate), 'yy.MM.dd (E)', { locale: ko })}
                    </div>
                    <div className="log-song">{log.song || '(곡 없음)'}</div>
                    {unreadCount > 0 && (
                      <span className="comment-badge">{unreadCount}</span>
                    )}
                    <span className={`log-status ${log.status}`}>
                      {log.status === 'published' ? '발행됨' : '임시저장'}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* 상세/수정 패널 */}
      <div className="detail-panel">
        {!selectedStudent ? (
          <div className="empty-state">
            <p>학생을 선택하세요</p>
          </div>
        ) : !selectedLog && !isCreatingNew ? (
          <div className="empty-state">
            <p>수업 기록을 선택하거나</p>
            <button className="btn-primary" onClick={handleNewLog}>
              새 기록 작성
            </button>
          </div>
        ) : (
          <>
            <div className="panel-header">
              <button className="btn-back" onClick={() => window.history.back()}>←</button>
              <div className="header-title">
                <div className="header-eyebrow">
                  {selectedStudent?.name}
                  {formData.lessonDate && ` · ${format(new Date(formData.lessonDate), 'yyyy. M. d (E)', { locale: ko })}`}
                  {formData.lessonTime && ` ${formData.lessonTime}`}
                </div>
                <h2>
                  {isCreatingNew ? '새 수업 기록' : '수업 기록 수정'}
                </h2>
              </div>
              {selectedLog && (
                <div className="header-right">
                  <span className={`status-badge ${selectedLog.status}`}>
                    {selectedLog.status === 'published' ? '발행됨' : '임시저장'}
                  </span>
                  <button className="btn-delete-log" onClick={handleDelete}>
                    삭제
                  </button>
                </div>
              )}
            </div>
            <div className="form-content">
              <div className="form-row">
                <div className="form-field half">
                  <label>수업 날짜</label>
                  <input
                    type="date"
                    value={formData.lessonDate}
                    onChange={(e) => handleChange('lessonDate', e.target.value)}
                  />
                </div>

                <div className="form-field half">
                  <label>수업 시간</label>
                  <input
                    type="time"
                    value={formData.lessonTime}
                    onChange={(e) => handleChange('lessonTime', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>곡</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    value={formData.song}
                    onChange={(e) => handleChange('song', e.target.value)}
                    placeholder="곡 제목을 입력하세요"
                  />
                  <button
                    type="button"
                    className="btn-youtube-search"
                    onClick={() => {
                      if (formData.song.trim()) {
                        window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(formData.song + ' drum cover')}`, '_blank');
                      }
                    }}
                    disabled={!formData.song.trim()}
                    title="YouTube에서 검색"
                  >
                    YouTube
                  </button>
                </div>
              </div>

              <div className="form-field">
                <label>유튜브 링크</label>
                <input
                  type="text"
                  value={formData.songYoutubeUrl}
                  onChange={(e) => handleChange('songYoutubeUrl', e.target.value)}
                  placeholder="https://youtube.com/..."
                />
                {currentThumbnail && (
                  <a href={formData.songYoutubeUrl} target="_blank" rel="noopener noreferrer" className="youtube-preview">
                    <img src={currentThumbnail} alt="YouTube" />
                  </a>
                )}
              </div>

              <div className="form-field">
                <label>수업 내용</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => handleChange('content', e.target.value)}
                  placeholder="오늘 수업에서 배운 내용을 입력하세요"
                  rows={4}
                />
              </div>

              <div className="form-field">
                <label>과제</label>
                <textarea
                  value={formData.homework}
                  onChange={(e) => handleChange('homework', e.target.value)}
                  placeholder="과제를 입력하세요"
                  rows={3}
                />
              </div>

              <div className="form-field private-note">
                <label>비공개 메모 <span className="note-hint">(선생님만 볼 수 있음)</span></label>
                <textarea
                  value={formData.teacherNote}
                  onChange={(e) => handleChange('teacherNote', e.target.value)}
                  placeholder="학생 상태, 진도, 특이사항 등"
                  rows={3}
                />
              </div>

              {/* 댓글 섹션 */}
              {selectedLog && selectedLog.status === 'published' && (
                <div className="comments-section">
                  <div className="comments-header">
                    <label>댓글</label>
                    {selectedLog.comments && selectedLog.comments.length > 0 && (
                      <span className="comment-count">{selectedLog.comments.length}</span>
                    )}
                  </div>
                  <div className="comments-list">
                    {selectedLog.comments && selectedLog.comments.length > 0 ? (
                      selectedLog.comments.map(comment => (
                        <div key={comment.id} className={`comment ${comment.authorType}`}>
                          <div className="comment-header">
                            <span className="author">
                              {comment.authorType === 'teacher'
                                ? (comment.teacher?.name || '선생님')
                                : (comment.student?.name || selectedStudent?.name || '학생')}
                            </span>
                            <span className="time">
                              {format(new Date(comment.createdAt), 'M.d HH:mm')}
                            </span>
                            {comment.authorType === 'student' && comment.isReadByTeacher && (
                              <span className="read-indicator">확인함</span>
                            )}
                            {comment.authorType === 'teacher' && editingCommentId !== comment.id && (
                              <div className="comment-actions">
                                <button
                                  className="btn-edit"
                                  onClick={() => handleStartEdit(comment)}
                                >
                                  수정
                                </button>
                                <button
                                  className="btn-delete"
                                  onClick={() => handleDeleteComment(comment.id)}
                                >
                                  삭제
                                </button>
                              </div>
                            )}
                          </div>
                          {editingCommentId === comment.id ? (
                            <div className="comment-edit-form">
                              <input
                                type="text"
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveEdit();
                                  }
                                }}
                                autoFocus
                              />
                              <div className="edit-actions">
                                <button className="btn-cancel" onClick={handleCancelEdit}>
                                  취소
                                </button>
                                <button
                                  className="btn-save"
                                  onClick={handleSaveEdit}
                                  disabled={!editCommentText.trim()}
                                >
                                  저장
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="comment-message">{linkifyText(comment.message)}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="no-comments">댓글이 없습니다</div>
                    )}
                  </div>
                  <div className="reply-form">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="답글 입력..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddReply();
                        }
                      }}
                    />
                    <button
                      onClick={handleAddReply}
                      disabled={!replyText.trim() || submittingReply}
                    >
                      {submittingReply ? '전송중...' : '전송'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                {saving ? '저장 중...' : '임시저장'}
              </button>
              <button
                className="btn-primary"
                onClick={() => handleSave(true)}
                disabled={saving}
              >
                {saving ? '저장 중...' : '저장 및 발행'}
              </button>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default LessonLog;
